/*
  COMPONENT: Facets Runtime

  Owns:
  - Ajax collection filter and sort updates
  - Product grid replacement

  Does not own:
  - Product card markup
*/

(() => {
  const parser = new DOMParser();
  const gridSelector = '[data-collection-grid]';
  const facetsSelector = '[data-facets-form]';
  const headerSelector = '.main-collection__header';
  const ajaxLinkSelector = '[data-collection-filter-link]';
  const statusSelector = '[data-collection-status]';
  const facetsOpenSelector = '[data-facets-open]';
  const facetsCloseSelector = '[data-facets-close]';
  const facetsPanelSelector = '[data-facets-panel]';
  const facetsClearSelector = '[data-facets-clear], .facets__active-tag a';
  const categoryNavSourceSelector = '.watch-bands-guide__categories';
  const categoryLinkSourceSelector = '[data-sticky-category-source]';
  const categoryTargetSelector = '[data-facets-categories]';
  const collectionSectionSelector = '[data-collection-section-id]';
  const cachedSections = new Map();
  const pendingSections = new Map();
  const cacheLifetime = 2 * 60 * 1000;
  const cacheLimit = 12;
  let lastFacetsTrigger = null;
  let requestVersion = 0;
  let cacheEpoch = 0;
  let prefetchTimer = null;

  function cacheKey(url) {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  }

  function rememberSection(url, markup) {
    const key = cacheKey(url);
    cachedSections.delete(key);
    cachedSections.set(key, { ...markup, savedAt: Date.now() });
    while (cachedSections.size > cacheLimit) cachedSections.delete(cachedSections.keys().next().value);
  }

  function cachedSection(url) {
    const key = cacheKey(url);
    const entry = cachedSections.get(key);
    if (!entry) return null;
    if (Date.now() - entry.savedAt > cacheLifetime) {
      cachedSections.delete(key);
      return null;
    }
    cachedSections.delete(key);
    cachedSections.set(key, entry);
    return entry;
  }

  function cacheCurrentSection() {
    const section = document.querySelector(collectionSectionSelector);
    if (section) rememberSection(window.location.href, { html: section.outerHTML, title: document.title });
  }

  async function fetchCollectionSection(url) {
    const sectionId = document.querySelector(collectionSectionSelector)?.dataset.collectionSectionId;
    if (sectionId) {
      const sectionUrl = new URL(url, window.location.origin);
      sectionUrl.searchParams.set('section_id', sectionId);
      const response = await fetch(sectionUrl.toString(), { credentials: 'same-origin' });
      if (response.ok) {
        const html = await response.text();
        if (parser.parseFromString(html, 'text/html').querySelector(gridSelector)) return { html, title: '' };
      }
    }

    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
    const html = await response.text();
    const doc = parser.parseFromString(html, 'text/html');
    const section = doc.querySelector(collectionSectionSelector);
    if (!section?.querySelector(gridSelector)) throw new Error('Collection response did not include a product grid.');
    return { html: section.outerHTML, title: doc.title };
  }

  function loadCollectionSection(url) {
    const cached = cachedSection(url);
    if (cached) return Promise.resolve(cached);

    const key = cacheKey(url);
    if (pendingSections.has(key)) return pendingSections.get(key);

    const thisEpoch = cacheEpoch;
    const request = fetchCollectionSection(url)
      .then((markup) => {
        if (thisEpoch === cacheEpoch) rememberSection(url, markup);
        return markup;
      })
      .finally(() => {
        if (pendingSections.get(key) === request) pendingSections.delete(key);
      });
    pendingSections.set(key, request);
    return request;
  }

  function scheduleCollectionPrefetch(link) {
    if (!link?.href || !document.querySelector(gridSelector)) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || cachedSection(url)) return;
    window.clearTimeout(prefetchTimer);
    prefetchTimer = window.setTimeout(() => {
      loadCollectionSection(url).catch(() => {});
    }, 90);
  }

  function removeShopifyEditorAttributes(element) {
    [element, ...element.querySelectorAll('*')].forEach((node) => {
      node.getAttributeNames().forEach((name) => {
        if (name.startsWith('data-shopify')) node.removeAttribute(name);
      });
    });
  }

  function createCategoryLink(href, label, accent = '') {
    const link = document.createElement('a');
    const text = document.createElement('span');
    link.className = 'facets__category-link';
    link.href = href;
    link.dataset.collectionFilterLink = '';
    if (accent) link.style.setProperty('--card-accent', accent);
    text.textContent = label;
    link.append(text);
    return link;
  }

  function syncStickyCategories() {
    const navSource = document.querySelector(categoryNavSourceSelector);
    const linkSources = Array.from(document.querySelectorAll(categoryLinkSourceSelector));

    document.querySelectorAll(categoryTargetSelector).forEach((target) => {
      if (!navSource && linkSources.length === 0) {
        target.replaceChildren();
        target.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();
      if (navSource) {
        Array.from(navSource.children).forEach((item) => {
          const clone = item.cloneNode(true);
          clone.classList.add('facets__category-link');
          removeShopifyEditorAttributes(clone);
          fragment.append(clone);
        });
        target.setAttribute('aria-label', navSource.getAttribute('aria-label') || target.getAttribute('aria-label') || '');
      } else {
        const baseUrl = target.dataset.facetsBaseUrl;
        const baseLabel = target.dataset.facetsBaseLabel;
        if (baseUrl && baseLabel) fragment.append(createCategoryLink(baseUrl, baseLabel));

        linkSources.forEach((source) => {
          const label = source.dataset.stickyCategoryLabel;
          if (!source.href || !label) return;
          const accent = source.closest('[style*="--card-accent"]')?.style.getPropertyValue('--card-accent') || '';
          fragment.append(createCategoryLink(source.href, label, accent));
        });
      }

      target.replaceChildren(fragment);
      target.hidden = target.children.length === 0;
    });
  }

  function openFacets(form, trigger) {
    const panel = form?.querySelector(facetsPanelSelector);
    if (!form || !panel) return;

    lastFacetsTrigger = trigger;
    form.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    panel.removeAttribute('inert');
    document.documentElement.classList.add('facets-panel-is-open');

    window.requestAnimationFrame(() => {
      panel.querySelector(facetsCloseSelector)?.focus({ preventScroll: true });
    });
  }

  function closeFacets(form = document.querySelector('.facets--sticky.is-open'), options = {}) {
    if (!form) return;

    const panel = form.querySelector(facetsPanelSelector);
    const trigger = form.querySelector(facetsOpenSelector) || lastFacetsTrigger;
    form.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
    panel?.setAttribute('aria-hidden', 'true');
    panel?.setAttribute('inert', '');

    if (!document.querySelector('.facets--sticky.is-open')) {
      document.documentElement.classList.remove('facets-panel-is-open');
    }

    if (options.restoreFocus !== false && trigger?.isConnected) {
      trigger.focus({ preventScroll: true });
    }
    lastFacetsTrigger = null;
  }

  function trapFacetsFocus(event, panel) {
    const focusable = Array.from(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'),
    ).filter((element) => element.getClientRects().length > 0);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function buildUrl(form) {
    const url = new URL(form.action || window.location.href, window.location.origin);
    url.search = '';

    const data = new FormData(form);
    data.forEach((value, key) => {
      if (value !== '') url.searchParams.append(key, value);
    });

    return url;
  }

  function setActiveCollectionLinks(url) {
    const currentUrl = new URL(url, window.location.origin);
    const currentPath = currentUrl.pathname.replace(/\/$/, '') || '/';

    document.querySelectorAll(ajaxLinkSelector).forEach((link) => {
      const linkUrl = new URL(link.href, window.location.origin);
      const linkPath = linkUrl.pathname.replace(/\/$/, '') || '/';
      const isActive = linkPath === currentPath && (!linkUrl.search || linkUrl.search === currentUrl.search);

      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  function replaceContent(doc, selector) {
    const current = document.querySelector(selector);
    const next = doc.querySelector(selector);
    if (!current || !next) return false;

    current.getAttributeNames().forEach((name) => {
      if (!next.hasAttribute(name)) current.removeAttribute(name);
    });
    Array.from(next.attributes).forEach((attribute) => {
      current.setAttribute(attribute.name, attribute.value);
    });
    current.innerHTML = next.innerHTML;
    return true;
  }

  function reinitializeGrid(grid) {
    if (window.CurtisTheme?.initialize) window.CurtisTheme.initialize(grid);
    if (window.CurtisMotion?.initialize) window.CurtisMotion.initialize(grid);
  }

  function canEnhanceLink(link, event) {
    if (!link || !link.href) return false;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;

    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return false;

    return Boolean(document.querySelector(gridSelector));
  }

  async function updateCollectionUrl(url, options = {}) {
    const targetUrl = new URL(url, window.location.origin);
    const grid = document.querySelector(gridSelector);
    if (!grid) {
      window.location.href = targetUrl.toString();
      return;
    }

    const thisRequest = ++requestVersion;
    const readyMarkup = cachedSection(targetUrl);
    closeFacets(document.querySelector('.facets--sticky.is-open'), { restoreFocus: false });
    setActiveCollectionLinks(targetUrl);

    if (options.scroll) {
      document.querySelector('#collection-products')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (!readyMarkup) {
      grid.setAttribute('aria-busy', 'true');
      document.querySelectorAll(ajaxLinkSelector).forEach((link) => link.setAttribute('aria-busy', 'true'));
      const status = document.querySelector(statusSelector);
      if (status) status.textContent = grid.dataset.collectionLoadingMessage || '';
    }

    try {
      const markup = readyMarkup || await loadCollectionSection(targetUrl);
      if (thisRequest !== requestVersion) return;
      const doc = parser.parseFromString(markup.html, 'text/html');
      const nextGrid = doc.querySelector(gridSelector);
      if (!nextGrid) throw new Error('Collection response did not include a product grid.');

      replaceContent(doc, gridSelector);
      replaceContent(doc, facetsSelector);
      replaceContent(doc, headerSelector);
      syncStickyCategories();
      reinitializeGrid(grid);

      document.title = doc.querySelector(collectionSectionSelector)?.dataset.collectionPageTitle || markup.title || document.title;
      const status = document.querySelector(statusSelector);
      if (status) {
        status.textContent = '';
        window.requestAnimationFrame(() => {
          status.textContent = grid.dataset.collectionResultMessage || '';
        });
      }

      if (options.push !== false) window.history.pushState({}, '', targetUrl.toString());
      setActiveCollectionLinks(targetUrl);

      if (options.focusResults) grid.focus({ preventScroll: true });
    } catch (error) {
      if (thisRequest === requestVersion) window.location.href = targetUrl.toString();
    } finally {
      if (thisRequest === requestVersion) {
        grid.removeAttribute('aria-busy');
        document.querySelectorAll(ajaxLinkSelector).forEach((link) => link.removeAttribute('aria-busy'));
      }
    }
  }

  async function updateCollection(form) {
    const grid = document.querySelector(gridSelector);
    if (!grid) {
      form.submit();
      return;
    }

    await updateCollectionUrl(buildUrl(form), { focusResults: true });
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(facetsSelector);
    if (!form) return;
    event.preventDefault();
    updateCollection(form);
  });

  document.addEventListener('change', (event) => {
    const form = event.target.closest(facetsSelector);
    if (!form || !event.target.matches('[data-facets-sort]')) return;
    updateCollection(form);
  });

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest(facetsOpenSelector);
    if (openButton) {
      openFacets(openButton.closest('[data-facets-root]'), openButton);
      return;
    }

    const closeButton = event.target.closest(facetsCloseSelector);
    if (closeButton) {
      closeFacets(closeButton.closest('[data-facets-root]'));
      return;
    }

    const clearLink = event.target.closest(facetsClearSelector);
    if (canEnhanceLink(clearLink, event)) {
      event.preventDefault();
      updateCollectionUrl(new URL(clearLink.href, window.location.origin), { focusResults: true });
      return;
    }

    const link = event.target.closest(ajaxLinkSelector);
    if (!canEnhanceLink(link, event)) return;

    event.preventDefault();
    window.clearTimeout(prefetchTimer);
    updateCollectionUrl(new URL(link.href, window.location.origin), { scroll: true, focusResults: true });
  });

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'touch') return;
    const link = event.target.closest(ajaxLinkSelector);
    if (link && !link.contains(event.relatedTarget)) scheduleCollectionPrefetch(link);
  });

  document.addEventListener('pointerout', (event) => {
    const link = event.target.closest(ajaxLinkSelector);
    if (link && !link.contains(event.relatedTarget)) window.clearTimeout(prefetchTimer);
  });

  document.addEventListener('focusin', (event) => {
    const link = event.target.closest(ajaxLinkSelector);
    if (link) scheduleCollectionPrefetch(link);
  });

  document.addEventListener('keydown', (event) => {
    const openForm = document.querySelector('.facets--sticky.is-open');
    if (!openForm) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeFacets(openForm);
      return;
    }

    if (event.key === 'Tab') {
      const panel = openForm.querySelector(facetsPanelSelector);
      if (panel) trapFacetsFocus(event, panel);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target?.matches?.(collectionSectionSelector) || event.target?.querySelector?.(collectionSectionSelector)) {
      requestVersion += 1;
      cacheEpoch += 1;
      cachedSections.clear();
      pendingSections.clear();
    }
    closeFacets(document.querySelector('.facets--sticky.is-open'), { restoreFocus: false });
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.matches?.(collectionSectionSelector) || event.target?.querySelector?.(collectionSectionSelector)) {
      requestVersion += 1;
      cacheEpoch += 1;
      cachedSections.clear();
      pendingSections.clear();
      cacheCurrentSection();
    }
    syncStickyCategories();
    setActiveCollectionLinks(window.location.href);
  });

  window.addEventListener('popstate', () => {
    if (!document.querySelector(gridSelector)) return;
    updateCollectionUrl(new URL(window.location.href), { push: false });
  });

  cacheCurrentSection();
  syncStickyCategories();
  setActiveCollectionLinks(window.location.href);
})();
